import {Switch} from '@mui/material'
import {FormikErrors, useFormik} from 'formik'
import {
  Product,
  ProductLink,
  useEventProducts,
} from 'organization/Event/Products/EventProductsProvider'
import React from 'react'
import {toast} from 'react-toastify'
import styled from 'styled-components'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {useI18n} from 'i18n/I18nContext'

type FormValuesTypes = {
  name: Product['name']
  description: Product['description']
  price: Product['price']
  status: Product['status']
  links: ProductLink[]
  type: Product['type']
  need_phone: Product['need_phone']
  need_name: Product['need_name']
  need_address: Product['need_address']
  quantity: Product['quantity']
}

export const UpdateProductModal = (props: {
  show: boolean
  product?: Product | null
  onClose: () => void
}) => {
  const {show, product, onClose} = props
  const {t} = useI18n()

  const {addProduct, updateProduct, loading} = useEventProducts()

  const {handleSubmit, handleChange, setFieldValue, values} = useFormik({
    initialValues: product ?? {
      name: '',
      description: '',
      price: '',
      status: true,
      type: 'digital',
      need_phone: true,
      need_name: false,
      need_address: false,
      quantity: 0,
      links: [
        {
          name: '',
          url: '',
        },
      ],
    },
    enableReinitialize: true,
    onSubmit: async (values: FormValuesTypes) => {
      const product: Product = values

      const result = props.product
        ? await updateProduct(product)
        : await addProduct(product)

      if (result === true) {
        toast.success(
          props.product
            ? t('orgProductModal.toastUpdated')
            : t('orgProductModal.toastAdded'),
        )
        onClose()
      }
    },
  })

  // const toggleProductType = () => {
  //   setFieldValue('type', values.type === 'digital' ? 'physical' : 'digital')
  // }

  const addLink = () => {
    setFieldValue('links', [
      ...values.links,
      {
        name: '',
        url: '',
      },
    ])
  }

  const updateLinkName = (linkKey: number, value: string) => {
    setFieldValue(
      'links',
      values.links.map((link, index) =>
        index === linkKey
          ? {
              ...link,
              name: value,
            }
          : link,
      ),
    )
  }

  const updateLinkUrl = (linkKey: number, value: string) => {
    setFieldValue(
      'links',
      values.links.map((link, index) =>
        index === linkKey
          ? {
              ...link,
              url: value,
            }
          : link,
      ),
    )
  }

  return (
    <>
      <div
        onClick={onClose}
        className={`offcanvas__overlay ${show ? 'overlay-open' : ''}`}
      ></div>
      <section className={`speaker__popup-area larger ${show ? 'open' : ''}`}>
        <div className="popup__wrapper larger">
          <div className="popup__title-wrapper">
            <h3 className="popup__title">
              {product
                ? t('orgProductModal.titleEdit')
                : t('orgProductModal.titleAdd')}
            </h3>
          </div>
          <div className="popup__input-wrapper">
            <form onSubmit={handleSubmit}>
              <FieldsHolder>
                <LeftSide>
                  <div className="singel__input-field mb-15">
                    <label className="input__field-label">
                      {t('orgProductModal.status')}
                    </label>
                    <Switch
                      defaultChecked={values.status}
                      onChange={(e: any) => {
                        setFieldValue('status', e.target.checked)
                      }}
                    />
                  </div>

                  <div className="singel__input-field mb-15">
                    <label className="input__field-email">
                      {t('orgProductModal.name')} <br />
                    </label>
                    <input
                      type="name"
                      name="name"
                      defaultValue={values.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="event__input mb-15">
                    <label className="input__field-text">
                      {t('orgProductModal.description')}
                    </label>

                    <textarea
                      defaultValue={values.description}
                      name="description"
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <div className="singel__input-field mb-15">
                    <label className="input__field-email">
                      {t('orgProductModal.price')}
                      <br />
                    </label>
                    <input
                      type="number"
                      name="price"
                      defaultValue={values.price}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </LeftSide>
                <RightSide>
                  {values.type === 'digital' && (
                    <ProductLinks
                      links={values.links}
                      updateLinkName={updateLinkName}
                      updateLinkUrl={updateLinkUrl}
                      addLink={addLink}
                    />
                  )}
                  {values.type === 'physical' && (
                    <PhysicalProduct
                      setFieldValue={setFieldValue}
                      values={values}
                    />
                  )}
                </RightSide>
              </FieldsHolder>

              <button
                type="submit"
                disabled={loading}
                className={
                  loading ? 'disableBtnStyle w-100' : 'input__btn w-100'
                }
              >
                {product ? (
                  <>{t('orgProductModal.submitEdit')}</>
                ) : (
                  <>
                    <i className="fa-regular fa-plus"></i>{' '}
                    {t('orgProductModal.submitAdd')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  )
}

const PhysicalProduct = (props: {
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => Promise<void> | Promise<FormikErrors<FormValuesTypes>>
  values: FormValuesTypes
}) => {
  const {values, setFieldValue} = props
  const {t} = useI18n()

  return (
    <>
      <div className="singel__input-field mb-15">
        <label className="input__field-email">
          {t('orgProductModal.stock')}{' '}
          <i style={{fontSize: 12}}>{t('orgProductModal.stockHint')}</i>
          <br />
        </label>
        <input
          type="number"
          min={0}
          step={1}
          defaultValue={values.quantity}
          onChange={(e: any) => {
            setFieldValue('quantity', e.target.value)
          }}
          required
        />
      </div>

      <div className="singel__input-field mb-15">
        <Switch
          disabled={true}
          defaultChecked={values.need_phone}
          onChange={(e: any) => {
            setFieldValue('need_phone', e.target.checked)
          }}
        />
        <label className="input__field-label">
          {t('orgProductModal.reqPhone')}
        </label>
      </div>
      <div className="singel__input-field mb-15">
        <Switch
          defaultChecked={values.need_name}
          onChange={(e: any) => {
            setFieldValue('need_name', e.target.checked)
          }}
        />
        <label className="input__field-label">
          {t('orgProductModal.reqName')}
        </label>
      </div>
      <div className="singel__input-field mb-15">
        <Switch
          defaultChecked={values.need_address}
          onChange={(e: any) => {
            setFieldValue('need_address', e.target.checked)
          }}
        />
        <label className="input__field-label">
          {t('orgProductModal.reqAddress')}
        </label>
      </div>
    </>
  )
}

const ProductLinks = (props: {
  links: ProductLink[]
  updateLinkName: (linkKey: number, value: string) => void
  updateLinkUrl: (linkKey: number, value: string) => void
  addLink: () => void
}) => {
  const {links, updateLinkName, updateLinkUrl, addLink} = props
  const {t} = useI18n()

  return (
    <div className="singel__input-field mb-15">
      <label className="input__field-text">{t('orgProductModal.links')}</label>
      {links.map((link, index) => {
        return (
          <LinkHolder key={index}>
            <label className="input__field-text">
              {t('orgProductModal.linkN').replace('{n}', String(index + 1))}
            </label>
            <LinkInputs>
              <input
                className="mb-20"
                placeholder={t('orgProductModal.linkNamePh')}
                onChange={(e) => {
                  updateLinkName(index, e.target.value)
                }}
                defaultValue={link.name}
              />
              <LinkUrlHolder>
                <input
                  className="mb-20"
                  placeholder={t('orgProductModal.linkUrlPh')}
                  onChange={(e) => {
                    updateLinkUrl(index, e.target.value)
                  }}
                  defaultValue={link.url}
                  style={{flex: 1}}
                />
                <TestButton
                  type="button"
                  className="input__btn"
                  onClick={() => window.open(link.url, '_blank')}
                >
                  <OpenInNewIcon fontSize="small" />{' '}
                  {t('orgProductModal.testLink')}
                </TestButton>
              </LinkUrlHolder>
            </LinkInputs>
          </LinkHolder>
        )
      })}
      <button
        type="button"
        className="unfield__input-btn w-100 mb-20"
        onClick={addLink}
      >
        <i className="fa-regular fa-plus"></i>
        {t('orgProductModal.addLink')}
      </button>
    </div>
  )
}

const FieldsHolder = styled.div`
  display: flex;
  gap: 15px;
`

const LeftSide = styled.div`
  flex: 1;
`

const RightSide = styled.div`
  flex: 1;
`

export const LinkHolder = styled.div`
  background: #ebebeb;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 10px;

  & > label {
    margin-bottom: 0px;
  }
`

export const LinkInputs = styled.div`
  & > input,
  & > div > input {
    height: 40px;
    margin: 5px;
  }
`

export const LinkUrlHolder = styled.div`
  display: flex;
  gap: 10px;
`

export const TestButton = styled.button`
  height: 37px;
  margin-top: 6px;
`
